// apps/app/src/lib/i18n.ts
//
// The dashboard's languages — the widget's four, so a clinic has one set across
// both. Each person picks their own; it is saved on their account and follows
// them to any device. Before they pick, the browser's language decides.
//
// Text lives in src/locales/<language>/<namespace>.json, one namespace per
// part of the dashboard (common for the frame, then one per feature), loaded
// only when a page that needs it opens. English is the source and the
// fallback: a string missing in another language shows in English, never as
// a raw key.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

export const LANGUAGES = [
  { code: 'en', label: 'English', intl: 'en-GB' },
  { code: 'es', label: 'Español', intl: 'es-ES' },
  { code: 'de', label: 'Deutsch', intl: 'de-DE' },
  { code: 'bn', label: 'বাংলা', intl: 'bn-BD' },
] as const;

export type Language = (typeof LANGUAGES)[number]['code'];

const STORAGE_KEY = 'gs.locale';

export function isLanguage(value: unknown): value is Language {
  return LANGUAGES.some((l) => l.code === value);
}

/** Remembered choice, then the browser's language, then English. */
function initialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    /* Private window: fall through to the browser's language. */
  }
  const browser = navigator.language.split('-')[0];
  return isLanguage(browser) ? browser : 'en';
}

void i18n
  .use(initReactI18next)
  .use(resourcesToBackend((language: string, namespace: string) => import(`../locales/${language}/${namespace}.json`)))
  .init({
    lng: initialLanguage(),
    fallbackLng: 'en',
    supportedLngs: LANGUAGES.map((l) => l.code),
    ns: ['common'],
    defaultNS: 'common',
    fallbackNS: 'common',
    interpolation: { escapeValue: false }, // React already escapes.
    returnNull: false,
    react: { useSuspense: true },
  });

i18n.on('languageChanged', (language) => {
  document.documentElement.lang = language;
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    /* Nothing to remember it in; the account still has it. */
  }
});
document.documentElement.lang = i18n.language;

/** The current language as an Intl locale, for dates, numbers and money. */
export function intlLocale(): string {
  return LANGUAGES.find((l) => l.code === i18n.language)?.intl ?? 'en-GB';
}

export default i18n;
